<template>
  <div>
    <TransitionRoot as="template" :show="sidebarOpen">
      <Dialog class="relative z-10 xl:hidden" @close="sidebarOpen = false">
        <TransitionChild as="template" enter="transition-opacity ease-linear duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="transition-opacity ease-linear duration-300" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 bg-gray-900/80" />
        </TransitionChild>

        <div class="fixed inset-0 flex">
          <TransitionChild as="template" enter="transition ease-in-out duration-300 transform" enter-from="-translate-x-full" enter-to="translate-x-0" leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0" leave-to="-translate-x-full">
            <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
              <TransitionChild as="template" enter="ease-in-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in-out duration-300" leave-from="opacity-100" leave-to="opacity-0">
                <div class="absolute left-full top-0 flex w-16 justify-center pt-5">
                  <button type="button" class="-m-2.5 p-2.5" @click="sidebarOpen = false">
                    <span class="sr-only">Close sidebar</span>
                    <XMarkIcon class="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </TransitionChild>
              <!-- Sidebar component, swap this element with another sidebar if you like -->
              <div class="flex grow flex-col gap-y-5 overflow-y-auto bg-jananavy-900 px-6 pb-2">
                <img class="w-[10rem] mt-6 block" src="@/assets/artwork/jana-logo-white.svg" alt="JANA" />
                <img class="w-[13rem] mt-6 block" src="@/assets/artwork/platform-logo-white.svg" alt="Steward" />
                <div class='bg-janared-400 text-white px-3 py-2 rounded-md' v-if='shouldShowSuperAdmin()'>
                  Super Admin
                </div>
                <nav class="flex flex-1 flex-col mt-6">
                  <ul role="list" class="flex flex-1 flex-col gap-y-7">
                    <li>
                      <AuthenticatedLayoutMenu :activeMenuItem='props.activeMenuItem' />
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Static sidebar for desktop -->
    <div class="hidden xl:fixed xl:inset-y-0 xl:z-10 xl:flex xl:w-72 xl:flex-col">
      <!-- Sidebar component, swap this element with another sidebar if you like -->
      <div class="flex grow flex-col gap-y-5 overflow-y-auto bg-jananavy-900 px-6">
        <img class="w-[10rem] mt-6 block" src="@/assets/artwork/jana-logo-white.svg" alt="JANA" />
        <img class="w-[13rem] mt-6 block" src="@/assets/artwork/platform-logo-white.svg" alt="Steward" />
        <div class='bg-janared-400 text-white px-3 py-2 rounded-md' v-if='shouldShowSuperAdmin()'>
          Super Admin
        </div>
        <div class='bg-janaturq-400 text-white px-3 py-2 rounded-md' v-if='shouldShowOrganisation()'>
          {{ getOrganisationName() }}
        </div>
        <nav class="flex flex-1 flex-col mt-6">
          <ul role="list" class="flex flex-1 flex-col gap-y-7">
            <li>
              <AuthenticatedLayoutMenu :activeMenuItem='props.activeMenuItem' />
            </li>
            <li class="-mx-6 mt-auto">
              <button href="#" class="flex items-center gap-x-4 px-6 py-3 text-sm/6 font-semibold text-white hover:bg-janaemer-400 w-full" @click='invokeUserPanel'>
                <UserCircleIcon class="h-8 w-8 text-white shrink-0" aria-hidden='true' />
                <div class='text-left text-xs/4 min-w-0 truncate'>
                  <span class="sr-only">Choose Organisation</span>
                  <span aria-hidden="true">{{ getSessionName() }}</span><br />
                  <span aria-hidden="true">{{ getEmailAddress() }}</span>
                </div>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <div class="sticky top-0 z-10 flex items-center gap-x-6 bg-jananavy-900 px-4 py-4 shadow-sm sm:px-6 xl:hidden">
      <button type="button" class="-m-2.5 p-2.5 text-jananavy-200 xl:hidden" @click="sidebarOpen = true">
        <span class="sr-only">Open sidebar</span>
        <Bars3Icon class="h-6 w-6" aria-hidden="true" />
      </button>
      <div class="flex-1 text-sm/6 font-semibold text-white truncate">{{ props.title }}</div>
      <button @click='invokeUserPanel' class='flex items-center gap-2 text-white text-xs'>
        <span class="sr-only">Choose Organisation</span>
        <span aria-hidden="true" class='truncate block max-w-32'>{{ getSessionName() }}</span>
        <UserCircleIcon class="h-8 w-8 text-white shrink-0" aria-hidden='true' />
      </button>
    </div>

    <main class="xl:pl-72">
      <slot />
    </main>
  </div>
</template>

<script setup>
  
import { ref } from 'vue';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import {
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon
} from '@heroicons/vue/24/outline';

import AuthenticatedLayoutMenu from '@/components/layouts/authenticated-layout-menu.vue';

import { getSessionName, getEmailAddress, presentModal, shouldShowSuperAdmin, getOrganisationName, shouldShowOrganisation } from '@/kernel/session';

const props = defineProps({
  activeMenuItem: String,
  title: String
});

const sidebarOpen = ref(false);

const invokeUserPanel = () => {
  presentModal();
}

</script>